/**
 * kdwService — KDW-0 / F-SENTINEL-KDW-001
 *
 * Kairos Data Warehouse (KDW) ingestion path — multi-domain substrate.
 * Extends the sentinel skeleton to cover 11 record types across 7 domains.
 *
 * Iceberg-style: every record is append-only, carries embedded schemaVersion,
 * domain, and partitionKey so queries can time-travel without migrations.
 *
 * Polaris-style catalog writes are handled by the CF kdwCatalogService.
 * This service writes to sentinel_kdw_ingestion for backward compatibility;
 * the canonical collection (kdw_records) is owned by the CF ingest endpoint.
 *
 * Architecture rules:
 *   - All Firestore writes via DataService — never Firebase SDK directly.
 *   - tenantId on every document.
 *   - softDeleted: null on all new documents.
 *   - EventBus via events/{eventId} Firestore writes.
 *   - No PII in summary fields.
 *
 * KDW_VERSION = '1.1.0'
 */

import DataService from '../DataService';

export const KDW_VERSION = '1.1.0';

export const VALID_RECORD_TYPES = [
  // Sentinel domain
  'agent_action',
  'lineage_record',
  'trust_update',
  'security_event',
  'audit_event',
  // Operations domains
  'load_event',
  'carrier_event',
  'quote_event',
  'customs_event',
  // Extended domains
  'iot_event',
  'consent_event',
];

/**
 * Domain mapping — derived from recordType.
 * Determines which logical domain owns this record type.
 */
export const DOMAIN_MAP = {
  agent_action:    'sentinel',
  lineage_record:  'sentinel',
  trust_update:    'sentinel',
  security_event:  'sentinel',
  audit_event:     'sentinel',
  load_event:      'loads',
  carrier_event:   'carriers',
  quote_event:     'quotes',
  customs_event:   'customs',
  iot_event:       'iot',
  consent_event:   'consent',
};

/**
 * Derive domain from recordType.
 * @param {string} recordType
 * @returns {string}
 */
export function deriveDomain(recordType) {
  return DOMAIN_MAP[recordType] || 'sentinel';
}

/**
 * Generate a KDW record ID.
 * Format: KDW_{base36-timestamp}_{random8}
 * @returns {string}
 */
export function generateKdwId() {
  const now = Date.now();
  const rand = Math.random().toString(36).slice(2, 10).toUpperCase();
  return `KDW_${now.toString(36).toUpperCase()}_${rand}`;
}

/**
 * Derive ISO date partition key (YYYY-MM-DD) from an ISO timestamp.
 * @param {string} isoTimestamp
 * @returns {string}
 */
export function derivePartitionKey(isoTimestamp) {
  return isoTimestamp.slice(0, 10);
}

/**
 * Validate an inbound KDW record.
 * @param {object} record
 * @throws {Error} if validation fails
 */
export function validateRecord(record) {
  if (!record.tenantId) throw new Error('kdwService: tenantId required');
  if (!record.recordType) throw new Error('kdwService: recordType required');
  if (!VALID_RECORD_TYPES.includes(record.recordType)) {
    throw new Error(`kdwService: unknown recordType "${record.recordType}"`);
  }
}

/**
 * Ingest a KDW record into the legacy-compat sentinel_kdw_ingestion collection.
 * Also emits kdw.record.ingested to EventBus.
 *
 * @param {object} record
 * @param {string}  record.tenantId
 * @param {string}  record.recordType      - one of VALID_RECORD_TYPES
 * @param {string}  [record.sourceRef]     - Firestore path of originating record
 * @param {object}  [record.summary]       - analytics subset, no PII
 * @param {string}  [record.timestamp]     - ISO-8601; defaults to now
 * @param {string}  [record.schemaVersion] - defaults to '1.0.0'
 * @param {string}  [record.ingestMode]    - 'realtime' | 'batch'; defaults to 'realtime'
 * @returns {Promise<string>} kdwId
 */
export async function ingestTelemetry(record) {
  validateRecord(record);

  const kdwId          = generateKdwId();
  const timestamp      = record.timestamp || new Date().toISOString();
  const schemaVersion  = record.schemaVersion || '1.0.0';
  const domain         = deriveDomain(record.recordType);
  const partitionKey   = derivePartitionKey(timestamp);
  const ingestMode     = record.ingestMode || 'realtime';

  const kdwRecord = {
    kdwId,
    tenantId:      record.tenantId,
    recordType:    record.recordType,
    domain,
    schemaVersion,
    partitionKey,
    ingestMode,
    sourceRef:     record.sourceRef || null,
    summary:       record.summary || {},
    kdwVersion:    KDW_VERSION,
    ingestedAt:    timestamp,
    softDeleted:   null,
  };

  // Write to legacy-compat collection (backward compatibility with sentinel consumers)
  await DataService.set(
    `sentinel_kdw_ingestion/${record.tenantId}/records/${kdwId}`,
    kdwRecord
  );

  // Emit kdw.record.ingested to EventBus
  const eventId = `EVT_KDW_${Date.now().toString(36).toUpperCase()}_${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  await DataService.set(`events/${eventId}`, {
    eventId,
    eventType:   'kdw.record.ingested',
    tenantId:    record.tenantId,
    kdwId,
    domain,
    recordType:  record.recordType,
    schemaVersion,
    partitionKey,
    emittedAt:   timestamp,
    softDeleted: null,
  });

  return kdwId;
}

/**
 * Batch-ingest multiple KDW records in parallel.
 * Failures on individual records are logged but do not abort the batch.
 *
 * @param {object[]} records
 * @returns {Promise<PromiseSettledResult[]>}
 */
export async function ingestBatch(records) {
  const results = await Promise.allSettled(records.map(r => ingestTelemetry(r)));
  const failures = results.filter(r => r.status === 'rejected');
  if (failures.length > 0) {
    console.warn(`[kdwService] ${failures.length}/${records.length} batch records failed ingestion`);
  }
  return results;
}

export default { ingestTelemetry, ingestBatch, generateKdwId, deriveDomain, derivePartitionKey, validateRecord };
